import React, { useState } from 'react';
import { Tabs, Button, Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import RootLayout from '@/components/layouts/RootLayout';
import { ContainerFluid, Section } from '@/components/layouts';
import { ContentBackground } from '@/components/content';
import { ContentWrapper, ContentHeader, ContentBody } from '@/components/content';
import { ROLES_PERMISSIONS } from '@/constants';
import { API } from '@/services/api.service';
import ParserConfigsPanel from './_components/ParserConfigsPanel';
import FormatAnalyzerPanel from './_components/FormatAnalyzerPanel';
import TestPlaygroundPanel from './_components/TestPlaygroundPanel';

function SmartParserPage() {
  const { t } = useTranslation(['tables', 'menus', 'forms']);
  const [activeTab, setActiveTab] = useState('configs');

  // Fetch parser configurations
  const { data: configsData, isLoading: configsLoading, refetch: refetchConfigs } = useQuery({
    queryKey: ['smart-parser-configs'],
    queryFn: async () => {
      try {
        const response = await API.smartParser.getConfigs();
        return response;
      } catch (error) {
        console.error('Failed to fetch parser configs:', error);
        return { data: [] };
      }
    },
  });

  const configs = configsData?.data || [];

  const tabItems = [
    {
      key: 'configs',
      label: 'Configurations',
      children: (
        <ParserConfigsPanel
          configs={configs}
          isLoading={configsLoading}
          onRefresh={refetchConfigs}
        />
      ),
    },
    {
      key: 'analyzer',
      label: 'Learn Mode',
      children: (
        <FormatAnalyzerPanel
          onConfigCreated={refetchConfigs}
        />
      ),
    },
    {
      key: 'playground',
      label: 'Parse Mode',
      children: (
        <TestPlaygroundPanel
          configs={configs}
        />
      ),
    },
  ];

  return (
    <RootLayout metadata={{ title: t('smartParser', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />

      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('smartParser', { ns: 'menus' })}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setActiveTab('analyzer')}
              >
                New Configuration
              </Button>
            </ContentHeader>

            <ContentBody>
              <div className="with-y-overflow">
                <div className="p-4">
                  <Alert
                    message="Smart Parser - EML Processing"
                    description="Upload EML files containing Excel timesheet attachments. In Learn Mode, AI analyzes the format and generates a parser configuration. In Parse Mode, use saved configurations to process timesheets and match employees with AB Soft."
                    type="info"
                    showIcon
                    closable
                    className="mb-4"
                  />

                  <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    className="smart-parser-tabs"
                  />
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>
    </RootLayout>
  );
}

const SmartParser = withPrivateRoute(
  SmartParserPage,
  ROLES_PERMISSIONS.SMART_PARSER.VIEW
);

export default SmartParser;
